/**
 * Zod schema for the one-time payment form. Messages are i18n keys, resolved through `t()` by the
 * field components, matching `paymentAccount.schema.ts`.
 *
 * Both bounds are injected rather than hardcoded: `maxAmount` comes from the `maximumPaymentAmount`
 * query so the client mirrors what the API enforces, and `now` is injectable so the Eastern-Time
 * date rule is testable without freezing the system clock.
 */
import { z } from 'zod';
import { isAfterTodayInEastern, parseAmount } from './oneTimePayment.format';

export const ONE_TIME_PAYMENT_VALIDATION_KEYS = {
  amountRequired: 'payments.validation.amountRequired',
  amountInvalid: 'payments.validation.amountInvalid',
  amountOutOfRange: 'payments.validation.amountOutOfRange',
  accountRequired: 'payments.validation.accountRequired',
  accountExpired: 'payments.validation.accountExpired',
  dateRequired: 'payments.validation.dateRequired',
  datePast: 'payments.validation.datePast',
  datePastAccountExpiry: 'payments.validation.datePastAccountExpiry',
} as const;

/** The smallest amount the gateway accepts, and what the design's range copy quotes. */
export const MIN_PAYMENT_AMOUNT = 0.01;

export interface OneTimePaymentFormValues {
  /** Held as a string because it is a text input with a `$` prefix, parsed on validate. */
  amount: string;
  paymentProfileId: string;
  /** ISO `yyyy-MM-dd` from a native date input. */
  scheduleDate: string;
}

interface OneTimePaymentSchemaArgs {
  maxAmount: number;
  /** Ids of accounts that cannot be charged, so selecting one fails validation rather than the API. */
  expiredAccountIds?: string[];
  /**
   * Account id → the last ISO date that account can be charged, for accounts that expire. Legacy
   * blocked scheduling past a card's expiry client-side, and nothing else catches it: the API's
   * failure codes are only `AMOUNT_EXCEEDS_MAXIMUM` and `NOT_FOUND`, so the payment would simply
   * fail to process later with no signal to the member.
   */
  lastChargeableDates?: Record<string, string>;
  now?: Date;
}

export function makeOneTimePaymentSchema({
  maxAmount,
  expiredAccountIds = [],
  lastChargeableDates = {},
  now = new Date(),
}: OneTimePaymentSchemaArgs) {
  return z
    .object({
      amount: z.string(),
      paymentProfileId: z.string(),
      scheduleDate: z.string(),
    })
    .superRefine((values, ctx) => {
      const amount = parseAmount(values.amount);
      if (values.amount.trim() === '') {
        ctx.addIssue({ code: 'custom', path: ['amount'], message: ONE_TIME_PAYMENT_VALIDATION_KEYS.amountRequired });
      } else if (amount === null) {
        ctx.addIssue({ code: 'custom', path: ['amount'], message: ONE_TIME_PAYMENT_VALIDATION_KEYS.amountInvalid });
      } else if (amount < MIN_PAYMENT_AMOUNT || amount > maxAmount) {
        ctx.addIssue({ code: 'custom', path: ['amount'], message: ONE_TIME_PAYMENT_VALIDATION_KEYS.amountOutOfRange });
      }

      if (values.paymentProfileId === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['paymentProfileId'],
          message: ONE_TIME_PAYMENT_VALIDATION_KEYS.accountRequired,
        });
      } else if (expiredAccountIds.includes(values.paymentProfileId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['paymentProfileId'],
          message: ONE_TIME_PAYMENT_VALIDATION_KEYS.accountExpired,
        });
      }

      if (values.scheduleDate === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['scheduleDate'],
          message: ONE_TIME_PAYMENT_VALIDATION_KEYS.dateRequired,
        });
      } else if (!isAfterTodayInEastern(values.scheduleDate, now)) {
        ctx.addIssue({ code: 'custom', path: ['scheduleDate'], message: ONE_TIME_PAYMENT_VALIDATION_KEYS.datePast });
      } else {
        const lastChargeable = lastChargeableDates[values.paymentProfileId];
        if (lastChargeable && values.scheduleDate > lastChargeable) {
          ctx.addIssue({
            code: 'custom',
            path: ['scheduleDate'],
            message: ONE_TIME_PAYMENT_VALIDATION_KEYS.datePastAccountExpiry,
          });
        }
      }
    });
}
